import { findLastIndex } from 'lodash-es';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// These values reliably catch redirect loops on 6x throttled CPU on a laptop.
export const MAX_PAGE_CHANGES = 64;
export const EXPIRE_TIME_MSEC = 15 * 1000;

const recentPathnames: Array<{
  pathname: string;
  timestamp: number;
}> = [];

function addPathname(pathname: string) {
  const now = Date.now();
  recentPathnames.push({
    pathname,
    timestamp: now,
  });
  const expiredTimestamp = now - EXPIRE_TIME_MSEC;
  const lastOldIndex = findLastIndex(
    recentPathnames,
    ({ timestamp }) => timestamp < expiredTimestamp
  );
  if (lastOldIndex >= 0) {
    recentPathnames.splice(0, lastOldIndex + 1);
  }
}

/*
 * Detect when lots of route changes occur in a short period of time.
 * If the pathname changes often, such as in an infinite redirect loop, throw an exception
 */
export function useRepeatPathChangeDetection() {
  const { pathname } = useLocation();

  /**
   * `useEffect` is often dangerous and unnecessary. This usage needs review by #ux-programs team.
   * See https://getcolor.atlassian.net/browse/UXTASK-1406
   */
  // nosemgrep:semgrep-rules.ui-useEffect
  useEffect(() => {
    addPathname(pathname);
    if (recentPathnames.length > MAX_PAGE_CHANGES) {
      const lastPathnames = recentPathnames
        .filter((_, index) => index > MAX_PAGE_CHANGES - 4)
        .map(({ pathname }) => pathname);
      throw new Error(
        `At least ${MAX_PAGE_CHANGES} path changes detected. Most recently: ${lastPathnames.join(
          ' -> '
        )}`
      );
    }
  }, [pathname]);
}
